import { Button } from "@components/library";
import {
  createDiscoverFeedInterest,
  deleteDiscoverFeedInterest,
  getDiscoverFeedInterests,
  updateDiscoverFeedInterest,
} from "@requests/discoverFeedInterests";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import type {
  DiscoverFeedInterestShowResponse,
  DiscoverFeedInterestsIndexResponse,
} from "@tsTypes/discover_feed_interests";
import type { DiscoverFeedInterest } from "@tsTypes/discover_feed_interests/_discover_feed_interest";
import { track } from "@utils/appUtils";
import { formatDisplayDateWithTime } from "@utils/dateUtils";
import { toastErrorHandler } from "@utils/requests";
import { useRef, useState } from "react";
import styled from "styled-components";
import { DISCOVER_FEED_QUERY_KEY } from "../../views/Discover/hooks/useDiscovery";
import InterestBadge from "./InterestBadge";
import InterestFlyout from "./InterestFlyout";
import queryKeys from "./queryKeys";

export type DiscoverFeedInterestsVariant = "feed" | "onboarding";

interface UpdateDiscoverFeedInterestArgs {
  id: number;
  description: string;
}

interface DiscoverFeedInterestsProps {
  variant?: DiscoverFeedInterestsVariant;
}

const DiscoverFeedInterests = ({ variant = "feed" }: DiscoverFeedInterestsProps) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: queryKeys.getDiscoverFeedInterestsQueryKey(),
    queryFn: getDiscoverFeedInterests,
    refetchOnMount: false,
  });

  const createMutation = useMutation({
    mutationFn: (description: string) => createDiscoverFeedInterest(description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.getDiscoverFeedInterestsQueryKey() });
      queryClient.invalidateQueries({ queryKey: [DISCOVER_FEED_QUERY_KEY] });
    },
    onError: toastErrorHandler,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, description }: UpdateDiscoverFeedInterestArgs) =>
      updateDiscoverFeedInterest(id, description),
    onSuccess: (response: DiscoverFeedInterestShowResponse) => {
      queryClient.setQueryData<DiscoverFeedInterestsIndexResponse>(
        queryKeys.getDiscoverFeedInterestsQueryKey(),
        (previousData) => {
          if (!previousData) return previousData;
          return {
            discover_feed_interests: previousData.discover_feed_interests.map((interest) =>
              interest.id === response.discover_feed_interest.id
                ? response.discover_feed_interest
                : interest
            ),
          };
        }
      );
      queryClient.invalidateQueries({ queryKey: [DISCOVER_FEED_QUERY_KEY] });
    },
    onError: toastErrorHandler,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteDiscoverFeedInterest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.getDiscoverFeedInterestsQueryKey() });
      queryClient.invalidateQueries({ queryKey: [DISCOVER_FEED_QUERY_KEY] });
    },
    onError: toastErrorHandler,
  });

  const interests: DiscoverFeedInterest[] = data?.discover_feed_interests ?? [];

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingInterest, setEditingInterest] = useState<DiscoverFeedInterest | null>(null);
  const [editingAnchorElement, setEditingAnchorElement] = useState<HTMLElement | null>(null);
  const addAnchorElementRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  const openAddFlyout = () => {
    track(SegmentEventName.CLICK, {
      ui_component: "Add Interest Button",
      react_component: "DiscoverFeedInterests",
      variant,
    });
    setIsAddOpen(true);
  };

  const closeAddFlyout = () => {
    setIsAddOpen(false);
  };

  const handleAddSubmit = async (text: string) => {
    track(SegmentEventName.SUBMIT, {
      ui_component: "Add Interest Form Submit",
      text,
      react_component: "DiscoverFeedInterests",
      variant,
    });
    await createMutation.mutateAsync(text);
    closeAddFlyout();
  };

  const handleInterestBadgeClick = (interest: DiscoverFeedInterest, element: HTMLElement) => {
    setEditingInterest(interest);
    setEditingAnchorElement(element);
  };

  const closeEditFlyout = () => {
    setEditingInterest(null);
    setEditingAnchorElement(null);
  };

  const handleEditSave = async (text: string) => {
    if (!editingInterest) return;
    await updateMutation.mutateAsync({ id: editingInterest.id, description: text });
    closeEditFlyout();
  };

  const handleEditRemove = async () => {
    if (!editingInterest) return;
    await deleteMutation.mutateAsync(editingInterest.id);
    closeEditFlyout();
  };

  return (
    <>
      <InterestBadges>
        {interests.map((interest) => (
          <InterestBadge key={interest.id} interest={interest} onClick={handleInterestBadgeClick} />
        ))}
        {variant === "onboarding" && (
          <Button
            leftIconName="Add"
            onClick={openAddFlyout}
            ref={addAnchorElementRef}
            variant="ghost"
            size="xs"
            data-testid="add-interest-button"
          >
            Add interest
          </Button>
        )}
      </InterestBadges>
      {variant === "feed" && (
        <Button
          leftIconName="Add"
          onClick={openAddFlyout}
          ref={addAnchorElementRef}
          variant="outline"
          size="sm"
          width="fit-content"
          data-testid="add-interest-button"
        >
          Add interest
        </Button>
      )}
      <InterestFlyout
        mode="add"
        variant={variant}
        isOpen={isAddOpen}
        anchorElement={addAnchorElementRef.current}
        isSubmitLoading={createMutation.isLoading}
        onClose={closeAddFlyout}
        onSubmit={handleAddSubmit}
      />
      <InterestFlyout
        mode="edit"
        variant={variant}
        isOpen={Boolean(editingInterest)}
        anchorElement={editingAnchorElement}
        isSubmitLoading={updateMutation.isLoading}
        isRemoveLoading={deleteMutation.isLoading}
        initialValue={editingInterest?.description ?? ""}
        lastEdited={
          editingInterest ? formatDisplayDateWithTime(editingInterest.updated_at) : undefined
        }
        onClose={closeEditFlyout}
        onSubmit={handleEditSave}
        onRemove={handleEditRemove}
      />
    </>
  );
};

export default DiscoverFeedInterests;

const InterestBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;
